import { useMemo, useState, useEffect } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import yn from 'yn';
import { usePostHogFeatureFlag } from '@hooks/usePostHogFeatureFlag';
import { useAuth0User } from '@hooks/useAuth0User';
import { useLeagueContext } from '@hooks/useLeagueContext';
import { useAccessToken } from '@hooks/useAccessToken';
import { usePlayers } from '@hooks/usePlayers';
import {
  PriceRiseColumnAccessors,
  generatePriceRiseColumns,
  toPriceRiseRow
} from '@components/Table/table-columns';
import Table from '@components/Table';
import { isUserAuthenticated, playerPositionOptions } from '@lib/utils';
import { useIsAppView } from '@hooks/useIsAppView';
import {
  BodyRegular,
  BodySmallRegularBold,
  H1,
  H2
} from '@components/atoms/typography';
import SearchInput from '@components/SearchInput';
import Button from '@components/atoms/button';
import useWindowSize from '@hooks/useWindowSize';
import LoadingView from '@components/atoms/loading-view';
import { PostHogFeatureFlags } from '@lib/posthog-feature-flags';
import EndOfSeasonHoldingMessage from '@components/end-of-season-holding-message';
import FreeMemberModal from '@components/free-member-modal';
import Paywall from '@components/paywall';
import { useLoginLink } from '@hooks/useLoginLink';
import type { PlayerPosition } from '@api-service/players';
import { getPageSeoHead, PAGE_SEO } from '@lib/page-seo';

const POSITION_ID_TO_ENUM: Record<number, PlayerPosition> = {
  1: 'GK',
  2: 'DEF',
  3: 'MID',
  4: 'FWD'
};

const PRICE_RISES_PAGE_SIZE = 100;
const MAX_GAMEWEEK = 38;

const PriceRises = () => {
  // The second argument is the route path, not the WordPress slug - the
  // slug produced a malformed canonical/og:url by string concatenation.
  const pageHead = getPageSeoHead(
    '/fantasy-premier-league-price-rises',
    PAGE_SEO.priceRises
  );
  const signupLink = useLoginLink(undefined, { screen_hint: 'signup' });
  const { user, isLoading: authIsLoading } = useAuth0User();
  const hasHubAccount = isUserAuthenticated(user);
  const { accessToken } = useAccessToken();
  const { context: leagueContext, isLoading: contextIsLoading } =
    useLeagueContext();
  const currentGameweek = leagueContext?.currentGameweek?.number ?? null;
  const nextGameweek = leagueContext?.nextGameweek?.number ?? null;

  const { width } = useWindowSize();
  const isMobile = width < 768;

  const [filter, setFilter] = useState('');
  const [position, setPosition] = useState<{ value: number; label: string }>({
    value: 0,
    label: 'All Players'
  });
  const [isFreeAccountModalOpen, setIsFreeAccountModalOpen] = useState(false);
  const [tableKey, setTableKey] = useState(0);

  useEffect(() => {
    setTableKey((previousKey) => previousKey + 1);
  }, [hasHubAccount]);

  const { isAppView } = useIsAppView();

  const positionFilter =
    position.value === 0 ? undefined : POSITION_ID_TO_ENUM[position.value];

  const { data, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } =
    usePlayers({
      enabled: Boolean(nextGameweek),
      accessToken: accessToken ?? undefined,
      include: 'priceChange',
      limit: PRICE_RISES_PAGE_SIZE,
      position: positionFilter,
      search: filter.trim() || undefined,
      sortBy: 'priceChangeEstimatedChangeTime',
      sortDirection: 'asc'
    });

  const rows = useMemo(() => {
    if (!data) return [];
    return data.pages.flatMap((page) => page.data.map(toPriceRiseRow));
  }, [data]);

  const priceChangesIsClosedFlagValue = usePostHogFeatureFlag(
    PostHogFeatureFlags.priceChangesIsClosed,
    false
  );

  const priceChangesIsClosed = yn(process.env.NEXT_PUBLIC_IS_TEST)
    ? false
    : priceChangesIsClosedFlagValue;

  const fplSeasonIsClosed = usePostHogFeatureFlag(
    PostHogFeatureFlags.fplSeasonIsClosed,
    false
  );

  const isAwaitingGameweekOne = nextGameweek === 1;

  if (
    priceChangesIsClosed ||
    fplSeasonIsClosed ||
    currentGameweek === MAX_GAMEWEEK ||
    isAwaitingGameweekOne
  ) {
    // FFH-2063: pre-GW1 gets season-specific copy; logged-out visitors get
    // the exclusive-content banner (as on the my-team pages) so they can
    // sign up rather than a dead-end holding screen.
    if (isAwaitingGameweekOne && !authIsLoading && !hasHubAccount) {
      return (
        <>
          <Head>{pageHead}</Head>
          <div className="h-screen pt-12 p-4">
            <Paywall
              variant="embedded"
              title="Price Changes will be available after GW1 starts"
              subtitle="Fantasy Premier League price change predictions will be here once the season starts, and are free to use for FFH members."
              ctaText="Sign up here"
            />
          </div>
        </>
      );
    }

    if (isAwaitingGameweekOne) {
      return (
        <>
          <Head>{pageHead}</Head>
          <EndOfSeasonHoldingMessage
            heading="Price Changes"
            accentHeading="will be available after GW1 starts"
            status="Awaiting Gameweek 1"
            html={
              isAppView
                ? 'Fantasy Premier League price change predictions will be here once the season starts. In the meantime, take a look at the latest articles in the content section.'
                : "Fantasy Premier League price change predictions will be here once the season starts. In the meantime, take a look at the latest articles on our homepage <a href='https://www.fantasyfootballhub.co.uk/'>here</a>."
            }
          />
        </>
      );
    }

    return (
      <>
        <Head>{pageHead}</Head>
        <EndOfSeasonHoldingMessage
          pageTitle="Fantasy Premier League price change predictions"
          isAppView={isAppView}
        />
      </>
    );
  }

  const isInitialLoad =
    contextIsLoading || (isFetching && rows.length === 0 && !data);

  return (
    <>
      <Head>{pageHead}</Head>
      <div>
        <div className="relative px-2 pt-2 pb-20 bg-white sm:px-6 lg:pb-10 lg:px-8">
          <div className="relative mx-auto max-w-7xl pb-14">
            <H1 id="price-rises-heading" className="py-3 px-2 md:px-0">
              Fantasy Premier League price change predictions
            </H1>
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between my-3 shadow-100 rounded-lg pt-4 pb-4 md:pb-2 px-2 md:px-4">
              <div className="max-w-[350px] w-full ml-1 mb-4">
                <SearchInput
                  label="Search"
                  id="price-change-player-search"
                  type="search"
                  handleChange={(e) => {
                    if (!hasHubAccount) {
                      setIsFreeAccountModalOpen(true);
                      return;
                    }
                    setFilter(e.target.value);
                  }}
                  value={filter}
                  placeholder="Search for a player"
                  showSearchIcon={true}
                  showCloseIcon={false}
                  onClose={() => setFilter('')}
                />
              </div>
              <div className="flex flex-col flex-grow w-full mx-0 md:mx-5 mb-3">
                <BodyRegular className="mb-2">Position</BodyRegular>
                <div className="flex justify-between">
                  {playerPositionOptions.map((option, index) => (
                    <BodySmallRegularBold
                      data-cy={`player-position-select-${index}`}
                      onClick={() => {
                        if (!hasHubAccount) {
                          setIsFreeAccountModalOpen(true);
                          return;
                        }
                        setPosition(option);
                      }}
                      key={`${option.label}-${option.value}`}
                      className="flex-1 text-center mr-2 py-2 rounded-lg cursor-pointer transition"
                      textColor={`${
                        option.value === position?.value
                          ? 'text-neutral-50 bg-primary-600'
                          : 'text-success-800 bg-neutral-100'
                      }`}
                    >
                      {option.label}
                    </BodySmallRegularBold>
                  ))}
                </div>
              </div>
            </div>
            {isInitialLoad ? (
              <div className="h-[85vh] pt-5">
                <LoadingView
                  headerText="Loading"
                  loadingText="Getting the latest price change predictions..."
                  progressBarSeconds={5}
                />
              </div>
            ) : rows.length ? (
              <div className="my-3 shadow-100 rounded-lg p-2">
                <div
                  className={`relative overflow-x-scroll lg:overflow-visible ${
                    !hasHubAccount ? 'pointer-events-none' : ''
                  }`}
                >
                  <Table
                    key={tableKey}
                    data={rows}
                    columns={generatePriceRiseColumns({ isMobile }) ?? []}
                    initialPageSize={
                      !hasHubAccount ? 12 : PRICE_RISES_PAGE_SIZE
                    }
                    initialPageIndex={0}
                    hiddenColumns={[]}
                    sortBy={[
                      {
                        id: PriceRiseColumnAccessors.ChangeTime,
                        desc: false
                      }
                    ]}
                    showFilters={false}
                    isSearchActive={filter.length > 0}
                  />
                </div>
                {hasHubAccount && hasNextPage && (
                  <div className="flex justify-center mx-auto my-5">
                    <Button
                      onClick={() => {
                        if (!isFetchingNextPage) fetchNextPage();
                      }}
                      variant="primary"
                      className="mx-auto"
                      disabled={isFetchingNextPage}
                    >
                      {isFetchingNextPage ? 'Loading...' : 'Load more'}
                    </Button>
                  </div>
                )}
                {!hasHubAccount && (
                  <>
                    <div className="pointer-events-auto absolute z-20 inset-x-0 bottom-16 h-[75%] bg-gradient-to-t from-white to-transparent pointer-events-none">
                      <div className="flex justify-center items-center absolute z-30 px-4 py-16 top-[150px] md:top-[120px] left-1/2 transform -translate-x-1/2 w-full bg-white flex flex-col justify-center">
                        <div className="max-w-[700px] px-4">
                          <div className="flex items-center mb-1">
                            <H2>Sign up for free</H2>
                          </div>
                          <BodyRegular>
                            <Link
                              prefetch={false}
                              {...signupLink}
                              className="cursor-pointer text-primary-600 font-bold"
                            >
                              Create a free account{' '}
                            </Link>
                            to be able to access the price change predictions
                          </BodyRegular>
                        </div>
                      </div>
                    </div>
                    <div className="absolute inset-x-0 bottom-0 h-[60%] bg-gradient-to-r from-white to-transparent pointer-events-none" />
                  </>
                )}
              </div>
            ) : (
              <div className="text-center py-10">
                <BodyRegular>No players match the current filters.</BodyRegular>
              </div>
            )}
          </div>
          <FreeMemberModal
            isOpen={isFreeAccountModalOpen}
            setIsOpen={setIsFreeAccountModalOpen}
            title="Create a free account to see all player price changes"
            description="Once you've signed up, you'll have full access to all player price change data."
          />
        </div>
      </div>
    </>
  );
};

export default PriceRises;
